import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Banner, Button } from '@cowoo/shared/ui-web';
import { ApiProblem } from '@cowoo/shared';
import { api } from '../../lib/api';
import { Field, Switch, fieldClass } from '../../ui';

/**
 * The editable terminal settings of `08-door-protocol` § 8, as § 12 draws them.
 *
 * Only the keys the protocol document defines, and only the ones an operator
 * sets: `loc_short`, `term_short` and `timezone` identify the installation and
 * changing them from a form would break the guest QR check. Every field carries
 * the range the firmware enforces, so a value the terminal would reject is
 * refused before it is sent — and the server clamps `pulse_ms` again, because a
 * config asking for more than the cap is a mistake rather than something to
 * silently trim.
 */
const FIELDS = [
  { key: 'pulse_ms', unit: 'ms', min: 3000, max: 8000, step: 100 },
  { key: 'near_rssi', unit: 'dBm', min: -90, max: -40, step: 1 },
  { key: 'exit_rssi', unit: 'dBm', min: -90, max: -40, step: 1 },
  { key: 'tx_power', unit: 'dBm', min: -20, max: 8, step: 1 },
  { key: 'detected_timeout_s', unit: 's', min: 1, max: 120, step: 1 },
  { key: 'scan_timeout_s', unit: 's', min: 1, max: 300, step: 1 },
  { key: 'denied_display_s', unit: 's', min: 1, max: 60, step: 1 },
  { key: 'open_display_s', unit: 's', min: 1, max: 60, step: 1 },
  { key: 'door_held_open_s', unit: 's', min: 5, max: 600, step: 1 },
  { key: 'volume', unit: '', min: 0, max: 1, step: 0.1 },
] as const;

const TEXT_FIELDS = ['guest_hours', 'target_version'] as const;

export function TerminalConfig({
  id,
  config,
  configRev,
}: {
  id: string;
  config: Record<string, unknown>;
  configRev: number;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [outOfService, setOutOfService] = useState(false);
  const [problem, setProblem] = useState<ApiProblem | null>(null);

  useEffect(() => {
    setDraft(
      Object.fromEntries(
        [...FIELDS.map((f) => f.key), ...TEXT_FIELDS].map((key) => [key, String(config[key] ?? '')]),
      ),
    );
    setOutOfService(config.out_of_service === true);
  }, [config]);

  // The switch takes effect at once (the board's row), the fields with "Speichern". Sending it
  // with the form instead left a flipped switch unsaved and the terminal in service.
  const outOfServiceToggle = useMutation({
    mutationFn: async (next: boolean) => {
      const { error } = await api.PATCH('/admin/terminals/{id}/config', {
        params: { path: { id } },
        body: { out_of_service: next },
      });
      if (error) throw error;
    },
    onMutate: (next) => setOutOfService(next),
    onSuccess: () => {
      setProblem(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'terminals'] });
    },
    onError: (error) => {
      setOutOfService(config.out_of_service === true);
      setProblem(error instanceof ApiProblem ? error : null);
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {};
      for (const field of FIELDS) {
        const text = draft[field.key] ?? '';
        if (text !== '') body[field.key] = Number(text);
      }
      for (const key of TEXT_FIELDS) {
        const text = draft[key] ?? '';
        if (text !== '') body[key] = text;
      }
      const { error } = await api.PATCH('/admin/terminals/{id}/config', {
        params: { path: { id } },
        body,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setProblem(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'terminals'] });
    },
    onError: (error) => setProblem(error instanceof ApiProblem ? error : null),
  });

  const reset = () => {
    setDraft(
      Object.fromEntries(
        [...FIELDS.map((f) => f.key), ...TEXT_FIELDS].map((key) => [key, String(config[key] ?? '')]),
      ),
    );
  };

  /** A value outside the firmware's range is not sent. */
  const outOfRange = FIELDS.some((field) => {
    const text = draft[field.key] ?? '';
    if (text === '') return false;
    const value = Number(text);
    return Number.isNaN(value) || value < field.min || value > field.max;
  });

  return (
    <div className="border-line rounded-card mt-4 flex flex-col gap-3.5 border bg-white px-5 py-4">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-adminBody text-ink font-semibold">{t('admin.terminals.settings')}</p>
        <p className="text-adminSmall text-muted">{t('admin.terminals.configRev', { rev: configRev })}</p>
      </div>

      {problem ? <Banner tone="danger">{t(problem.i18nKey)}</Banner> : null}

      <div className="grid grid-cols-3 gap-3.5">
        {FIELDS.map((field) => (
          <Field
            key={field.key}
            label={t(`admin.terminals.config.${field.key}`)}
            hint={`${field.min} ${t('admin.terminals.to')} ${field.max}${field.unit ? ` ${field.unit}` : ''}`}
          >
            <input
              type="number"
              min={field.min}
              max={field.max}
              step={field.step}
              value={draft[field.key] ?? ''}
              onChange={(event) => setDraft({ ...draft, [field.key]: event.target.value })}
              className={fieldClass}
            />
          </Field>
        ))}
        {TEXT_FIELDS.map((key) => (
          <Field key={key} label={t(`admin.terminals.config.${key}`)}>
            <input
              value={draft[key] ?? ''}
              onChange={(event) => setDraft({ ...draft, [key]: event.target.value })}
              className={fieldClass}
            />
          </Field>
        ))}
      </div>

      {/* The board's row: a fog box with the words on the left and a switch
          on the right — a state that takes effect at once, not a form field. */}
      <div className="bg-fog flex items-center justify-between gap-4 rounded-xl px-3.5 py-3">
        <div>
          <p className="text-adminBody text-ink font-semibold">{t('admin.terminals.outOfService')}</p>
          {/* § 8 — remote open keeps working, which is the whole point: staff
              must still be able to let somebody in through a terminal that is
              refusing local credentials. */}
          <p className="text-muted text-xs">{t('admin.terminals.outOfServiceHint')}</p>
        </div>
        <Switch
          checked={outOfService}
          onChange={(next) => outOfServiceToggle.mutate(next)}
          label={t('admin.terminals.outOfService')}
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button size="sm" variant="secondary" onClick={reset}>
          {t('admin.settings.discard')}
        </Button>
        <Button size="sm" loading={save.isPending} disabled={outOfRange} onClick={() => save.mutate()}>
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}
